import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import LocationSelect from "@/components/common/LocationSelect";
import { useCategories, useConditions } from "@/hooks/useMeta";

const ALL = "__all";

const Section = ({ title, children }) => (
  <div className="space-y-2 border-b border-black/[0.08] pb-4"><p className="eyebrow">{title}</p>{children}</div>
);

function Chips({ options, selected, onToggle, prefix }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => {
        const on = selected.includes(o.value);
        return (
          <button key={o.value} type="button" title={o.title} data-testid={`${prefix}-${o.value}`} onClick={() => onToggle(o.value)}
            className={`rounded-full border px-2 py-1 text-xs ${on ? "border-[#0071e3] bg-[#0071e3]/15 text-orange-700" : "border-black/[0.08] text-zinc-500 hover:border-black/20"}`}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function SpecFilter({ field, params, set }) {
  const k = `f_${field.key}`;
  if (field.type === "number")
    return (
      <div className="grid grid-cols-2 gap-2">
        <Input data-testid={`filter-${field.key}-min`} type="number" placeholder={`Min ${field.unit || ""}`} value={params.get(`${k}_min`) || ""} onChange={(e) => set({ [`${k}_min`]: e.target.value })} />
        <Input data-testid={`filter-${field.key}-max`} type="number" placeholder={`Maks ${field.unit || ""}`} value={params.get(`${k}_max`) || ""} onChange={(e) => set({ [`${k}_max`]: e.target.value })} />
      </div>
    );
  if (field.type === "boolean")
    return <Switch data-testid={`filter-${field.key}`} checked={params.get(k) === "true"} onCheckedChange={(c) => set({ [k]: c ? "true" : "" })} />;
  if (field.options) {
    const sel = (params.get(k) || "").split("|").filter(Boolean);
    const toggle = (v) => { const n = sel.includes(v) ? sel.filter((x) => x !== v) : [...sel, v]; set({ [k]: n.join("|") }); };
    return <Chips prefix={`filter-${field.key}`} options={field.options.map((o) => ({ value: o, label: o }))} selected={sel} onToggle={toggle} />;
  }
  return <Input data-testid={`filter-${field.key}`} value={params.get(k) || ""} onChange={(e) => set({ [k]: e.target.value })} />;
}

/** All listing search filters. State lives in URL search params. */
export default function FilterPanel({ params, set, reset }) {
  const { data: cats = [] } = useCategories();
  const { data: schemes } = useConditions();
  const cat = cats.find((c) => c.slug === params.get("category"));
  const scheme = cat?.condition_scheme ? schemes?.[cat.condition_scheme] : null;
  const conds = (params.get("condition") || "").split(",").filter(Boolean);

  return (
    <div className="space-y-4" data-testid="filter-panel">
      <Section title="Kategori">
        <Select value={params.get("category") || ALL} onValueChange={(v) => set({ category: v === ALL ? "" : v }, true)}>
          <SelectTrigger data-testid="filter-category-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Tüm kategoriler</SelectItem>
            {cats.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </Section>
      <Section title="Konum">
        <LocationSelect allowAll prefix="filter" province={params.get("province")} district={params.get("district")}
          onChange={(p, d) => set({ province: p, district: d })} />
      </Section>
      <Section title="Durum">
        <Chips prefix="filter-state" selected={[params.get("item_state") || ""]}
          options={[{ value: "", label: "Tümü" }, { value: "new", label: "Sıfır" }, { value: "used", label: "İkinci el" }]}
          onToggle={(v) => set({ item_state: v })} />
      </Section>
      {scheme && params.get("item_state") !== "new" && (
        <Section title="Kondisyon sınıfı (satıcı beyanı)">
          <Chips prefix="filter-condition" selected={conds}
            options={scheme.classes.map((c) => ({ value: c.code, label: c.code, title: c.label }))}
            onToggle={(v) => set({ condition: (conds.includes(v) ? conds.filter((x) => x !== v) : [...conds, v]).join(",") })} />
          <ul className="space-y-0.5 text-[11px] text-zinc-500" data-testid="condition-legend">
            {scheme.classes.map((c) => <li key={c.code}><span className="font-mono text-zinc-700">{c.code}</span> — {c.label}</li>)}
          </ul>
        </Section>
      )}
      <Section title="Fiyat (₺)">
        <div className="grid grid-cols-2 gap-2">
          <Input aria-label="Minimum fiyat" data-testid="filter-price-min" type="number" placeholder="Min" value={params.get("price_min") || ""} onChange={(e) => set({ price_min: e.target.value })} />
          <Input aria-label="Maksimum fiyat" data-testid="filter-price-max" type="number" placeholder="Maks" value={params.get("price_max") || ""} onChange={(e) => set({ price_max: e.target.value })} />
        </div>
      </Section>
      {cat && (
        <div className="space-y-4" data-testid="spec-filters">
          <p className="font-display text-sm font-semibold text-[#0077ed]">{cat.name} teknik filtreleri</p>
          {cat.fields.filter((f) => f.filter).map((f) => (
            <div key={f.key} className="space-y-1.5"><Label className="text-xs text-zinc-500">{f.label}</Label><SpecFilter field={f} params={params} set={set} /></div>
          ))}
        </div>
      )}
      <Button variant="outline" className="w-full border-black/[0.08] bg-transparent" onClick={reset} data-testid="filter-reset-button">Filtreleri temizle</Button>
    </div>
  );
}
